<template>
  <div class="space-y-4 text-sm text-light">
    <!-- Global Filter -->
    <template v-if="enableGlobalFilter">
      <div class="flex-1">
        <textbox
          @input="onGlobalFilterChange"
          :placeholder="globalFilterPlaceholder!"
        />
      </div>
    </template>

    <div class="border rounded">
      <template v-if="enableColumnCustomization">
        <div
          class="flex justify-end p-0.5 border-b bg-gray-50 dark:bg-gray-800 rounded-t"
        >
          <Popover class="relative">
            <PopoverButton aria-label="Customize table">
              <Btn
                component="span"
                size="icon"
                :type="null"
                class="!p-1.5 text-light !bg-transparent"
              >
                <Icon icon="cog" class="w-4 h-4" />
              </Btn>
            </PopoverButton>

            <PopoverPanel
              class="absolute right-0 left-auto w-[220px] bg-white dark:bg-gray-900 border rounded"
            >
              <div class="grid gap-1 px-4 py-3">
                <Checkbox
                  label="Show All"
                  :model-value="table.getIsAllColumnsVisible()"
                  @update:model-value="toggleAllColumnsVisibility"
                />
                <hr class="m-0" />
                <template
                  v-for="column in table.getAllLeafColumns()"
                  :key="column.id"
                >
                  <Checkbox
                    :label="column.columnDef.header"
                    :model-value="column.getIsVisible()"
                    :disabled="column.id === 'name'"
                    @update:model-value="toggleColumnVisibility(column)"
                  />
                </template>
              </div>
            </PopoverPanel>
          </Popover>
        </div>
      </template>

      <PartTable
        :table="table"
        :enable-t-head="enableTHead"
        :enable-t-foot="enableTFoot"
        :wrapping-borders="wrappingBorders"
        :wrap-text="wrapText"
        @update:sort="
          (header, event) => header.column.getToggleSortingHandler()?.(event)
        "
      >
        <template
          v-for="(slot, index) in Object.keys($slots)"
          :key="index"
          #[slot]="{cell}"
        >
          <slot :name="slot" :cell="cell" :row="cell.row.original" />
        </template>
      </PartTable>

      <!-- Pagination -->
      <template v-if="enablePagination">
        <part-pagination
          @click:next="() => table.nextPage()"
          @click:previous="() => table.previousPage()"
          :can-previous="table.getCanPreviousPage()"
          :can-next="table.getCanNextPage()"
          :table="table"
        />
      </template>

      <!-- Total Results -->
      <template v-if="enableTotalResults">
        <part-total-results class="mt-4" :total-results="totalResults" />
      </template>
    </div>
  </div>
</template>

<script setup lang="ts" generic="TData">
  import {
    type Column,
    type ColumnDef,
    type ColumnOrderState,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    type SortingState,
    useVueTable,
    type VisibilityState,
  } from '@tanstack/vue-table';
  import {computed, ref, toRefs, watch} from 'vue';
  import PartTable from '@console/components/tables/PartTable.vue';
  import Textbox from '@/common/ui/components/Textbox.vue';
  import PartPagination from '@console/components/tables/PartPagination.vue';
  import PartTotalResults from '@console/components/tables/PartTotalResults.vue';
  import Icon from '@/common/ui/components/Icon.vue';
  import {Popover, PopoverButton, PopoverPanel} from '@headlessui/vue';
  import {Btn} from '@/common/ui/components';
  import Checkbox from '@/common/ui/components/Checkbox.vue';
  import type {Project} from '@/console';

  const props = withDefaults(
    defineProps<{
      id?: string;
      columns: ColumnDef<TData>[];
      data: Array<TData>;

      enablePagination?: boolean;
      enableGlobalFilter?: boolean;
      enableTotalResults?: boolean;
      enableSorting?: boolean;
      enableTHead?: boolean;
      enableTFoot?: boolean;
      enableColumnCustomization?: boolean;
      globalFilterPlaceholder?: string;

      wrappingBorders?: boolean;
      wrapText?: boolean;
    }>(),
    {
      id: 'data-table',
      enablePagination: false,
      enableSorting: true,
      enableGlobalFilter: false,
      enableTotalResults: false,
      enableTHead: true,
      enableTFoot: false,
      enableColumnCustomization: false,
      globalFilterPlaceholder: 'Search',

      wrappingBorders: true,
      wrapText: false,

      columns: () => [],
      data: () => [],
    }
  );

  const {
    data,
    enablePagination,
    enableGlobalFilter,
    enableTotalResults,
    enableSorting,
  } = toRefs(props);

  const stateId = computed(() => `table:${props.id}:column-visibility`);
  const initialState = localStorage.getItem(stateId.value)
    ? JSON.parse(localStorage.getItem(stateId.value)!)
    : {};
  const columnVisibility = ref<VisibilityState>(initialState);
  const columnOrder = ref<ColumnOrderState>([]);

  watch(columnVisibility, (newValue) => {
    localStorage.setItem(stateId.value, JSON.stringify(newValue));
  });

  const pagination = ref({
    pageIndex: 0,
    pageSize: 50,
  });

  const sorting = ref<SortingState>([]);

  function toggleColumnVisibility(column: Column<Project, any>) {
    columnVisibility.value = {
      ...columnVisibility.value,
      [column.id]: !column.getIsVisible(),
    };
  }

  function toggleAllColumnsVisibility(value: boolean) {
    columnVisibility.value = table.getAllLeafColumns().reduce((acc, column) => {
      if (column.id !== 'name') {
        acc[column.id] = value;
      }
      return acc;
    }, {} as VisibilityState);
  }

  const table = useVueTable({
    get data() {
      return data.value;
    },
    columns: props.columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: enablePagination.value
      ? getPaginationRowModel()
      : undefined,
    getSortedRowModel: getSortedRowModel(),
    debugTable: true,
    state: {
      get sorting() {
        return sorting.value;
      },
      get columnVisibility() {
        return columnVisibility.value;
      },
      get columnOrder() {
        return columnOrder.value;
      },
    },
    enableSorting,
    onSortingChange: (updaterOrValue) => {
      sorting.value =
        typeof updaterOrValue === 'function'
          ? updaterOrValue(sorting.value)
          : updaterOrValue;
    },
    initialState: {
      pagination: enablePagination.value ? pagination.value : undefined,
    },
  });

  const totalResults = computed(() => {
    return table.getFilteredRowModel().rows.length;
  });

  const onGlobalFilterChange = (event: InputEvent) => {
    const value = (event.target as HTMLInputElement).value;
    table.setGlobalFilter(value);
  };
</script>
